import { externalMapLink } from "../../../geo/service";
import ResumenGeografia from './ResumenGeografia';
// src/Components/modules/authorizations/Form/ViewAuthorizationDialog.js

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Typography,
  Descriptions,
  Button,
  Row,
  Col,
  Divider,
  Spin,
  Calendar,
  Tooltip,
  Tag,
  Alert,
} from "antd";
import {
  CommentOutlined,
  HomeOutlined,
  TeamOutlined,
  MedicineBoxOutlined,
  InfoCircleOutlined,
  BarChartOutlined,
  CarOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";
import dayjs from "../../../utils/dayjs";
import "./ViewAuthorizationDialog.css";
import TransfersModal from "../Authorizations/Form/TransfersModal";
import TrasladoDialog from "../CenterControl/TrasladoDialog";

const { Title } = Typography;

const ViewAuthorizationDialog = ({
  authorizationId,
  open,
  handleClose,
  versionData = null,
  isHistoricalVersion = false
}) => {
  const [authorization, setAuthorization] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTransfersModal, setShowTransfersModal] = useState(false);
  const [showTrasladoDialog, setShowTrasladoDialog] = useState(false);

  useEffect(() => {
    // Si es versión histórica, usar los datos proporcionados
    if (isHistoricalVersion && versionData) {
      // Procesar calendario
      let calendarioRaw = versionData.calendario;
      if (typeof calendarioRaw === "string" && calendarioRaw.trim() !== "") {
        versionData.calendario = JSON.parse(calendarioRaw);
      } else if (!Array.isArray(calendarioRaw)) {
        versionData.calendario = [];
      }
      setAuthorization(versionData);
      return;
    }

    const fetchAuthorization = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/autorizaciones/${authorizationId}`);
        const data = response.data;

        // Verificar si data.calendario es una cadena no vacía
        let calendarioRaw = data.calendario;
        if (typeof calendarioRaw === "string" && calendarioRaw.trim() !== "") {
          // Parsear el JSON ya que es una cadena válida
          data.calendario = JSON.parse(calendarioRaw);
        } else if (Array.isArray(calendarioRaw)) {
          // Si ya es un arreglo, no hacer nada
          data.calendario = calendarioRaw;
        } else {
          // Si es null, undefined, o cadena vacía, usar []
          data.calendario = [];
        }

        setAuthorization(data);
      } catch (error) {
        console.error("[ViewAuth] Error al obtener autorización:", error);
        console.error("Error al obtener la autorización:", error);
        console.error("Detalles del error:", {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
        });
      } finally {
        setLoading(false);
      }
    };

    if (authorizationId && !isHistoricalVersion) {
      fetchAuthorization();
    }

    return () => {
      setAuthorization(null);
    };
  }, [authorizationId, isHistoricalVersion, versionData]);

  // Actualiza la función de renderizado para usar cellRender
  const cellRender = (date, info) => {
    if (info.type === "date") {
      if (!authorization || !Array.isArray(authorization.calendario))
        return null;

      const dateString = dayjs(date).tz("America/Bogota").format("YYYY-MM-DD");
      if (authorization.calendario.includes(dateString)) {
        return (
          <Tooltip title="Día de Traslado">
            <div className="traslado-day">
              <CarOutlined style={{ color: "#fff" }} />
            </div>
          </Tooltip>
        );
      }
    }
    return null;
  };

  // Formatear fechas para mostrar
  const formatDate = (dateString) => {
    return dayjs(dateString).tz("America/Bogota").format("DD/MM/YYYY");
  };

  // Funciones para generar enlaces de Google Maps y Street View
  const googleMapsLink = (coordinates) => {
    // Las coordenadas vienen como array [longitud, latitud]
    const [lng, lat] = coordinates;
    return externalMapLink(lat,lng);
  };
  const googleStreetViewLink = (coordinates) => {
    // Las coordenadas vienen como array [longitud, latitud]
    const [lng, lat] = coordinates;
    return externalMapLink(lat,lng,'street_view');
  };

  // Estilos personalizados para títulos y descripciones
  const titleStyle = {
    background: "#0a2e5c",
    color: "white",
    padding: "6px",
    borderRadius: "4px",
    fontSize: "14px",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
  };

  const descriptionItemStyle = {
    marginBottom: "2px",
  };

  const labelStyle = {
    fontWeight: "bold",
    fontSize: "12px",
  };

  const contentStyle = {
    fontSize: "12px",
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        width="90%"
        maxWidth={1100}
        title={
          isHistoricalVersion ? (
            <span>
              <HistoryOutlined style={{ color: "#722ed1", marginRight: 8 }} />
              Version {authorization?.numero_version} - Autorizacion #{authorization?.autorizacion_id}
              <Tag color="purple" style={{ marginLeft: 12 }}>
                {authorization?.fecha_version
                  ? new Date(authorization.fecha_version).toLocaleString("es-CO")
                  : ""}
              </Tag>
            </span>
          ) : (
            `Autorizacion #${authorization?.id}`
          )
        }
        styles={{
          body: {
            maxHeight: "80vh",
            overflow: "auto",
          },
        }}
        centered
        destroyOnHidden
        footer={
          isHistoricalVersion
            ? [
                <Button key="close" onClick={handleClose}>
                  Cerrar
                </Button>,
              ]
            : [
                <Button
                  key="programar"
                  type="primary"
                  onClick={() => setShowTrasladoDialog(true)}
                  icon={<CarOutlined />}
                  disabled={authorization?.estado_autorizacion?.toLowerCase() !== 'activa'}
                >
                  Programar Traslado
                </Button>,
                <Button
                  key="transfers"
                  type="primary"
                  onClick={() => setShowTransfersModal(true)}
                  icon={<CarOutlined />}
                  disabled={!authorization?.traslados_count}
                >
                  {`Ver Traslados (${authorization?.traslados_count || 0})`}
                </Button>,
                <Button key="close" onClick={handleClose}>
                  Cerrar
                </Button>,
              ]
        }
      >
        {loading || !authorization ? (
          <div className="authorization-loading">
            <Spin />
          </div>
        ) : (
          <div className="authorization-content">
            {isHistoricalVersion && (
              <Alert
                message={`Version historica #${authorization.numero_version}`}
                description={
                  <>
                    Esta es una version anterior de la autorizacion guardada el{" "}
                    <strong>
                      {authorization.fecha_version
                        ? new Date(authorization.fecha_version).toLocaleString("es-CO")
                        : "N/A"}
                    </strong>
                    {authorization.usuario && (
                      <>
                        {" "}por <strong>{authorization.usuario.nombre}</strong>
                      </>
                    )}
                    . Los datos mostrados corresponden al estado de la autorizacion en ese momento.
                  </>
                }
                type="info"
                showIcon
                icon={<HistoryOutlined />}
                style={{ marginBottom: 16 }}
              />
            )}
            <ResumenGeografia authorizationId={authorizationId} historica={isHistoricalVersion} snapshot={authorization?.geografia_snapshot} />
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Title level={5} style={titleStyle}>
                  <MedicineBoxOutlined style={{ marginRight: "6px" }} />
                  MIPRES
                </Title>
                <Descriptions
                  column={1}
                  bordered
                  size="small"
                  styles={{
                    label: labelStyle,
                    content: contentStyle,
                  }}
                  style={{ marginBottom: "12px" }}
                >
                  <Descriptions.Item
                    label="Código MIPRES"
                    style={descriptionItemStyle}
                  >
                    {authorization.codigo_mipres ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="CUPS Código"
                    style={descriptionItemStyle}
                  >
                    {authorization.cups_codigo ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="CUPS Descripción"
                    style={descriptionItemStyle}
                  >
                    {authorization.cups_descripcion ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Número de Entrega"
                    style={descriptionItemStyle}
                  >
                    {authorization.numero_entrega ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Entregas Autorizadas"
                    style={descriptionItemStyle}
                  >
                    {authorization.entregas_autorizadas ?? "N/A"}
                  </Descriptions.Item>
                </Descriptions>

                <Title level={5} style={titleStyle}>
                  <InfoCircleOutlined style={{ marginRight: "6px" }} />
                  Información General
                </Title>
                <Descriptions
                  column={1}
                  bordered
                  size="small"
                  styles={{
                    label: labelStyle,
                    content: contentStyle,
                  }}
                  style={{ marginBottom: "12px" }}
                >
                  <Descriptions.Item
                    label="Cliente"
                    style={descriptionItemStyle}
                  >
                    {authorization.empresa ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Número de Pre-Autorización"
                    style={descriptionItemStyle}
                  >
                    {authorization.numero_pre_autorizacion ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Número de Autorización"
                    style={descriptionItemStyle}
                  >
                    {authorization.numero_autorizacion ?? "N/A"}
                  </Descriptions.Item>
                  {/* Mostrar números anteriores si existen */}
                  {authorization.numero_pre_autorizacion_anterior && (
                    <Descriptions.Item
                      label="Número de Pre-Autorización Anterior"
                      style={{
                        ...descriptionItemStyle,
                        backgroundColor: "#fff7e6"
                      }}
                    >
                      {authorization.numero_pre_autorizacion_anterior}
                    </Descriptions.Item>
                  )}
                  {authorization.numero_autorizacion_anterior && (
                    <Descriptions.Item
                      label="Número de Autorización Anterior"
                      style={{
                        ...descriptionItemStyle,
                        backgroundColor: "#fff7e6"
                      }}
                    >
                      {authorization.numero_autorizacion_anterior}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item
                    label="Propósito"
                    style={descriptionItemStyle}
                  >
                    {authorization.proposito ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Tipo de Autorización"
                    style={descriptionItemStyle}
                  >
                    {authorization.tipo_autorizacion ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Copago Total"
                    style={descriptionItemStyle}
                  >
                    {authorization.copago ?? "N/A"}
                  </Descriptions.Item>
                </Descriptions>

                <Title level={5} style={titleStyle}>
                  <TeamOutlined style={{ marginRight: "6px" }} />
                  Estado de la Autorización
                </Title>
                <Descriptions
                  column={1}
                  bordered
                  size="small"
                  styles={{
                    label: labelStyle,
                    content: contentStyle,
                  }}
                >
                  <Descriptions.Item
                    label="Inicio Fecha de Autorización"
                    style={descriptionItemStyle}
                  >
                    {authorization.inicio_fecha_autorizacion
                      ? formatDate(authorization.inicio_fecha_autorizacion)
                      : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Final Fecha de Autorización"
                    style={descriptionItemStyle}
                  >
                    {authorization.final_fecha_autorizacion
                      ? formatDate(authorization.final_fecha_autorizacion)
                      : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Estado de Autorización"
                    style={descriptionItemStyle}
                  >
                    {authorization.estado_autorizacion ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Categoría de Autorización"
                    style={descriptionItemStyle}
                  >
                    {authorization.categoria_autorizacion ?? "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              </Col>

              <Col xs={24} sm={24} md={12} lg={8}>
                <Title level={5} style={titleStyle}>
                  <TeamOutlined style={{ marginRight: "6px" }} />
                  Paciente
                </Title>
                <Descriptions
                  column={1}
                  bordered
                  size="small"
                  styles={{
                    label: labelStyle,
                    content: contentStyle,
                  }}
                  style={{ marginBottom: "12px" }}
                >
                  <Descriptions.Item
                    label="Nombre del Paciente:"
                    style={descriptionItemStyle}
                  >
                    {authorization.nombre_paciente ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Tipo de Documento:"
                    style={descriptionItemStyle}
                  >
                    {authorization.tipo_documento_paciente ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Número de Documento:"
                    style={descriptionItemStyle}
                  >
                    {authorization.numero_documento_paciente ?? "N/A"}
                  </Descriptions.Item>
                </Descriptions>

                <Title level={5} style={titleStyle}>
                  <CommentOutlined style={{ marginRight: "6px" }} />
                  Observaciones
                </Title>
                <Descriptions
                  column={1}
                  bordered
                  size="small"
                  styles={{
                    label: labelStyle,
                    content: contentStyle,
                  }}
                  style={{ marginBottom: "12px" }}
                >
                  <Descriptions.Item style={descriptionItemStyle}>
                    {authorization.observaciones ?? "N/A"}
                  </Descriptions.Item>
                </Descriptions>

                <Title level={5} style={titleStyle}>
                  <TeamOutlined style={{ marginRight: "6px" }} />
                  Tipo de Traslado
                </Title>
                <Descriptions
                  column={1}
                  bordered
                  size="small"
                  styles={{
                    label: labelStyle,
                    content: contentStyle,
                  }}
                  style={{ marginBottom: "12px" }}
                >
                  <Descriptions.Item
                    label="Tipo de Viaje"
                    style={descriptionItemStyle}
                  >
                    {authorization.tipo_viaje ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Tipo de Traslado"
                    style={descriptionItemStyle}
                  >
                    {authorization.tipo_traslado ?? "N/A"}
                  </Descriptions.Item>
                </Descriptions>

                <Title level={5} style={titleStyle}>
                  <BarChartOutlined style={{ marginRight: "6px" }} />
                  Estadísticas de Traslado
                </Title>
                <Descriptions
                  column={1}
                  bordered
                  size="small"
                  styles={{
                    label: labelStyle,
                    content: contentStyle,
                  }}
                >
                  <Descriptions.Item
                    label="Traslados Autorizados"
                    style={descriptionItemStyle}
                  >
                    {authorization.total_traslados_asignados ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Traslados Restantes"
                    style={descriptionItemStyle}
                  >
                    {authorization.traslados_restantes ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Traslados en Curso"
                    style={descriptionItemStyle}
                  >
                    {authorization.traslados_en_curso ?? 0}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Traslados Usados"
                    style={descriptionItemStyle}
                  >
                    {authorization.usados ?? 0}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Traslados Fallidos"
                    style={descriptionItemStyle}
                  >
                    {authorization.fallidos ?? 0}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Traslados Cancelados"
                    style={descriptionItemStyle}
                  >
                    {authorization.cancelados ?? 0}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Kilómetros Autorizados"
                    style={descriptionItemStyle}
                  >
                    {authorization.kilometros_autorizados ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Kilómetros por Servicios"
                    style={descriptionItemStyle}
                  >
                    {authorization.kilometros_por_servicios ?? "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              </Col>

              <Col xs={24} sm={24} md={24} lg={8}>
                <Title level={5} style={titleStyle}>
                  <HomeOutlined style={{ marginRight: "6px" }} />
                  Origen del Traslado
                </Title>
                <Descriptions
                  column={1}
                  bordered
                  size="small"
                  styles={{
                    label: labelStyle,
                    content: contentStyle,
                  }}
                  style={{ marginBottom: "12px" }}
                >
                  <Descriptions.Item
                    label="Lugar de Origen"
                    style={descriptionItemStyle}
                  >
                    {authorization.origen_institucion ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Departamento"
                    style={descriptionItemStyle}
                  >
                    {authorization.origen_departamento ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Ciudad/Municipio"
                    style={descriptionItemStyle}
                  >
                    {authorization.origen_ciudad ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Localidad"
                    style={descriptionItemStyle}
                  >
                    {authorization.origen_localidad ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Barrio"
                    style={descriptionItemStyle}
                  >
                    {authorization.origen_barrio ?? "N/A"}
                  </Descriptions.Item>
                  {authorization.origen_vereda_corregimiento && (
                    <Descriptions.Item
                      label="Vereda/Corregimiento"
                      style={descriptionItemStyle}
                    >
                      {authorization.origen_vereda_corregimiento}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item
                    label="Dirección"
                    style={descriptionItemStyle}
                  >
                    {authorization.origen_direccion ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Indicaciones"
                    style={descriptionItemStyle}
                  >
                    {authorization.origen_indicaciones ?? "N/A"}
                  </Descriptions.Item>
                  {authorization.origen_coordenadas && (
                    <Descriptions.Item
                      label="Ver Ubicación"
                      style={descriptionItemStyle}
                    >
                      <Button
                        type="primary"
                        href={googleMapsLink(
                          authorization.origen_coordenadas.coordinates
                        )}
                        target="_blank"
                        style={{ marginRight: "8px", marginBottom: "8px" }}
                        block
                      >
                        Google Maps
                      </Button>
                      <Button
                        type="default"
                        href={googleStreetViewLink(
                          authorization.origen_coordenadas.coordinates
                        )}
                        target="_blank"
                        block
                      >
                        Street View
                      </Button>
                    </Descriptions.Item>
                  )}
                </Descriptions>

                <Title level={5} style={titleStyle}>
                  <HomeOutlined style={{ marginRight: "6px" }} />
                  Destino del Traslado
                </Title>
                <Descriptions
                  column={1}
                  bordered
                  size="small"
                  styles={{
                    label: labelStyle,
                    content: contentStyle,
                  }}
                >
                  <Descriptions.Item
                    label="Lugar de Destino"
                    style={descriptionItemStyle}
                  >
                    {authorization.destino_institucion ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Departamento"
                    style={descriptionItemStyle}
                  >
                    {authorization.destino_departamento ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Ciudad/Municipio"
                    style={descriptionItemStyle}
                  >
                    {authorization.destino_ciudad ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Localidad"
                    style={descriptionItemStyle}
                  >
                    {authorization.destino_localidad ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Barrio"
                    style={descriptionItemStyle}
                  >
                    {authorization.destino_barrio ?? "N/A"}
                  </Descriptions.Item>
                  {authorization.destino_vereda_corregimiento && (
                    <Descriptions.Item
                      label="Vereda/Corregimiento"
                      style={descriptionItemStyle}
                    >
                      {authorization.destino_vereda_corregimiento}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item
                    label="Dirección"
                    style={descriptionItemStyle}
                  >
                    {authorization.destino_direccion ?? "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Indicaciones"
                    style={descriptionItemStyle}
                  >
                    {authorization.destino_indicaciones ?? "N/A"}
                  </Descriptions.Item>
                  {authorization.destino_coordenadas && (
                    <Descriptions.Item
                      label="Ver Ubicación"
                      style={descriptionItemStyle}
                    >
                      <Button
                        type="primary"
                        href={googleMapsLink(
                          authorization.destino_coordenadas.coordinates
                        )}
                        target="_blank"
                        style={{ marginRight: "8px", marginBottom: "8px" }}
                        block
                      >
                        Google Maps
                      </Button>
                      <Button
                        type="default"
                        href={googleStreetViewLink(
                          authorization.destino_coordenadas.coordinates
                        )}
                        target="_blank"
                        block
                      >
                        Street View
                      </Button>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Col>
            </Row>

            {authorization.tipo_autorizacion.toLowerCase() === "cerrada" && (
              <div style={{ marginTop: "24px" }}>
                <Divider />
                <Title level={5} style={titleStyle}>
                  <InfoCircleOutlined style={{ marginRight: "6px" }} />
                  Calendario de Autorización Cerrada
                </Title>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    overflowX: "auto",
                  }}
                >
                  <Calendar
                    value={
                      authorization.inicio_fecha_autorizacion
                        ? dayjs(authorization.inicio_fecha_autorizacion)
                        : dayjs()
                    }
                    validRange={
                      authorization.inicio_fecha_autorizacion &&
                      authorization.final_fecha_autorizacion
                        ? [
                            dayjs(authorization.inicio_fecha_autorizacion),
                            dayjs(authorization.final_fecha_autorizacion),
                          ]
                        : undefined
                    }
                    cellRender={cellRender}
                    fullscreen={false}
                    style={{ width: "100%" }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {authorization && showTransfersModal && (
        <TransfersModal
          autorizacionId={authorization.id}
          onClose={() => setShowTransfersModal(false)}
        />
      )}
      {authorization && (
        <TrasladoDialog
        open={showTrasladoDialog}
        onClose={() => setShowTrasladoDialog(false)}
        onSubmit={() => {
          setShowTrasladoDialog(false);
        }}
        initialPacienteId={authorization.paciente_id}
        initialAutorizacionId={authorization.id}
        disabledPacienteSelect={true}
        disabledAutorizacionSelect={true}
        />
      )}
    </>
  );
};

ViewAuthorizationDialog.propTypes = {
  authorizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  versionData: PropTypes.object,
  isHistoricalVersion: PropTypes.bool,
};

export default ViewAuthorizationDialog;
